import React, { useCallback } from 'react';
import Card from '@/components/UI/Card';
import FormSwitch from '@/components/UI/FormSwitch';
import { trpc } from '@/api/trpc.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import type { OptimisticPortfolioSettings } from './types';

interface PositionApprovalCardProps {
    portfolioId: string | undefined;
    optimisticSettings: OptimisticPortfolioSettings;
    setOptimisticSettings: React.Dispatch<React.SetStateAction<OptimisticPortfolioSettings>>;
    pendingMutations: Record<string, boolean>;
    setPendingMutations: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

const PositionApprovalCard: React.FC<PositionApprovalCardProps> = ({
    portfolioId,
    optimisticSettings,
    setOptimisticSettings,
    pendingMutations,
    setPendingMutations
}) => {
    const queryClient = useQueryClient();

    // Mutation for toggling manual approval
    const toggleManualApprovalMutation = useTrpcMutation(
        trpc.user.savePortfolioSetting.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries({
                    queryKey: trpc.user.fetchPortfolio.queryOptions({ id: parseInt(portfolioId || '0'), shouldFetchTrades: true }).queryKey
                });

                // Clear pending mutation flag
                setPendingMutations((prev) => ({
                    ...prev,
                    requireBuyApproval: false
                }));
            },
            onError: () => {
                // Revert optimistic update on error
                setOptimisticSettings((prev) => ({
                    ...prev,
                    requireBuyApproval: !prev.requireBuyApproval
                }));

                // Clear pending mutation flag
                setPendingMutations((prev) => ({
                    ...prev,
                    requireBuyApproval: false
                }));
            }
        }),
        {
            successMessage: 'Manual approval setting updated successfully',
            fallbackErrorMessage: 'Failed to update manual approval setting'
        }
    );

    const handleToggleManualApproval = useCallback(() => {
        // Get current value (use optimistic state)
        const currentValue = optimisticSettings.requireBuyApproval;

        // Prevent double-clicks
        if (pendingMutations['requireBuyApproval']) {
            return;
        }

        // Set pending mutation flag
        setPendingMutations((prev) => ({
            ...prev,
            requireBuyApproval: true
        }));

        // Update optimistic UI immediately
        setOptimisticSettings((prev) => ({
            ...prev,
            requireBuyApproval: !currentValue
        }));

        // Send request to server
        void toggleManualApprovalMutation.mutateAsync({
            portfolioId: parseInt(portfolioId || '0'),
            settingType: 'requireBuyApproval',
            value: { value: !currentValue }
        });
    }, [
        portfolioId,
        optimisticSettings.requireBuyApproval,
        toggleManualApprovalMutation,
        pendingMutations,
        setPendingMutations,
        setOptimisticSettings
    ]);

    return (
        <Card className="mb-4" title="Position Approval Rules">
            <FormSwitch
                checked={optimisticSettings.requireBuyApproval}
                disabled={pendingMutations['requireBuyApproval']}
                id="manuallyApproveBuyCheck"
                label="Manually Approve Purchases"
                onChange={handleToggleManualApproval}
            />
        </Card>
    );
};

export default PositionApprovalCard;
